/**
 * Program Enrollments API — TRUS-378 / P3.
 *
 * A Program Enrollment is one member x one program. It owns status, timers,
 * assignment, eligibility, units and authorization, because the VYC workbook's
 * decision S1 is that status is per PROGRAM, not per member: a member can be
 * Open/Enrolled in CHW Benefits and Closed at Outreach in APS simultaneously.
 *
 * Distinct from `getMemberEnrollments` in services/api/members.ts, which reads
 * the platform-wide enrollment lifecycle (`enrollment_status`, activities,
 * goals). This module reads the org-CONFIGURED record: `program_status_code`
 * validated against the org's own status graph, plus the configurable custom
 * fields stored in program_enrollment_forms.
 *
 * The field set is NOT declared here. `getFieldConfig()` returns it from
 * com_fields, so an admin adding a field makes it appear without a release.
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';
import { initializeActiveCboId } from '../../utils/auth';
import { API_VERSION } from './organizations';

// Typed against the evaluator that consumes them, not the member-profile copy:
// components/forms/fieldVisibility is the single source of truth for how a rule
// is interpreted, and its VisibilityRules is the shape isFieldVisible accepts.
import type { VisibilityRules } from '../../components/forms/fieldVisibility';

// ============================================================================
// TYPES
// ============================================================================

/** One configured field, as com_fields defines it. */
export interface EnrollmentFieldDef {
  field_id: number;
  name: string;
  /** com_fields.type: str | long_text | int | date | select | multi_select | checkbox | computed | ... */
  field_type: string;
  /** com_fields.friendly_name (there is no `label` column), or the per-org override. */
  display_name: string;
  is_required: boolean;
  is_hidden: boolean;
  /** True for a computed field with a formula — the server derives it, so never send it back. */
  is_readonly: boolean;
  sort_order: number;
  max_length?: number | null;
  help_text?: string | null;
  placeholder_text?: string | null;
  field_options?: Array<{ label: string; value: string }> | null;
  /** Conditional visibility flows ONLY through com_fields.visibility_rules. */
  visibility_rules?: VisibilityRules | null;
  computed_formula?: Record<string, any> | null;
}

export interface EnrollmentFieldCategory {
  category_key: string;
  category_name: string;
  category_sort: number;
  fields: EnrollmentFieldDef[];
}

export interface EnrollmentFieldConfig {
  record_type: string;
  categories: EnrollmentFieldCategory[];
  field_count: number;
}

export interface ProgramStatus {
  status_code: string;
  status_label: string;
  sort_order: number;
  status_color?: string | null;
  is_terminal: boolean;
  promotes_to_enrollment: boolean;
  /** The transition graph. The server enforces it; the UI only offers what it lists. */
  allowed_next_statuses: string[];
}

export interface ProgramStatusConfig {
  program_id: number;
  program_name: string;
  statuses: ProgramStatus[];
}

export interface EnrollableProgram {
  id: number;
  name: string;
  program_type?: string | null;
  is_active: boolean;
}

export interface ProgramEnrollment {
  id: number;
  member_id: number;
  program_id: number;
  program_name: string | null;
  /** NULL is the workbook's "blank" status: no enrollment attempted. Not an error. */
  program_status_code: string | null;
  status_label: string | null;
  is_terminal: boolean | null;
  allowed_next_statuses: string[];
  /** The constrained platform lifecycle value, kept in sync by a DB trigger. Read-only. */
  enrollment_status: string | null;
  status_effective_date: string | null;
  interest_shown_date: string | null;
  enrollment_date: string | null;
  disenrollment_date: string | null;
  last_contact_date: string | null;
  assigned_to: number | null;
  /** The promotora / specialist assigned to THIS program. */
  assigned_to_name: string | null;
  managed_care_plan: string | null;
  units_authorized: number | string | null;
  units_used: number | string | null;
  units_remaining: number | string | null;
  /** TRUS-762: the programme carries the doula visit allowance. Absent on older servers. */
  is_doula_program?: boolean;
  end_date?: string | null;
  custom_fields: Record<string, any>;
}

export interface StatusHistoryEntry {
  id: number;
  from_status: string | null;
  to_status: string | null;
  effective_date: string | null;
  /** 'api' | 'cron:outreach_closure' | 'cron:renewal' — a cron row is a machine decision. */
  source: string;
  reason: string | null;
  changed_by: number | null;
  changed_by_name: string | null;
  changed_at: string;
}

export interface CustomFieldValueInput {
  field_name: string;
  value: any;
}

export interface EnrollmentCreateInput {
  program_id: number;
  program_status_code?: string | null;
  status_effective_date?: string | null;
  interest_shown_date?: string | null;
  enrollment_date?: string | null;
  assigned_to?: number | null;
  native_fields?: Record<string, any>;
  custom_fields?: CustomFieldValueInput[];
}

export interface EnrollmentUpdateInput {
  native_fields?: Record<string, any>;
  custom_fields?: CustomFieldValueInput[];
}

// ============================================================================
// CLIENT
// ============================================================================

class ProgramEnrollmentsAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    initializeActiveCboId();
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Version': API_VERSION,
      },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // X-CBO-ID is what scopes every query server-side. Without it the API
      // falls back to the caller's default org, which on a multi-org account is
      // not necessarily the org whose member is on screen.
      if (cboId) {
        config.headers['X-CBO-ID'] = cboId;
      }
      return config;
    });
  }

  /** The configured enrollment fields. Render from this, never from a literal. */
  async getFieldConfig(): Promise<EnrollmentFieldConfig> {
    const { data } = await this.client.get<EnrollmentFieldConfig>(
      '/api/program-enrollments/field-config',
    );
    return data;
  }

  /** Configured statuses + the transition graph, per program. */
  async getStatusConfig(programId?: number): Promise<ProgramStatusConfig[]> {
    const { data } = await this.client.get<{ programs: ProgramStatusConfig[] }>(
      '/api/program-enrollments/status-config',
      { params: programId ? { program_id: programId } : undefined },
    );
    return data.programs || [];
  }

  async getPrograms(): Promise<EnrollableProgram[]> {
    const { data } = await this.client.get<{ programs: EnrollableProgram[] }>(
      '/api/program-enrollments/programs',
    );
    return data.programs || [];
  }

  async listForMember(memberId: number): Promise<ProgramEnrollment[]> {
    const { data } = await this.client.get<{ enrollments: ProgramEnrollment[] }>(
      `/api/program-enrollments/member/${memberId}`,
    );
    return data.enrollments || [];
  }

  async create(memberId: number, body: EnrollmentCreateInput): Promise<ProgramEnrollment> {
    const { data } = await this.client.post<ProgramEnrollment>(
      `/api/program-enrollments/member/${memberId}`,
      body,
    );
    return data;
  }

  async update(enrollmentId: number, body: EnrollmentUpdateInput): Promise<ProgramEnrollment> {
    const { data } = await this.client.patch<ProgramEnrollment>(
      `/api/program-enrollments/${enrollmentId}`,
      body,
    );
    return data;
  }

  /**
   * The only way status moves. The server enforces allowed_next_statuses, so a
   * rejection here is the org's own configuration talking, not a bug.
   */
  async changeStatus(
    enrollmentId: number,
    body: { program_status_code: string | null; effective_date?: string | null; reason?: string | null },
  ): Promise<ProgramEnrollment> {
    const { data } = await this.client.post<ProgramEnrollment>(
      `/api/program-enrollments/${enrollmentId}/status`,
      body,
    );
    return data;
  }

  async getStatusHistory(enrollmentId: number): Promise<StatusHistoryEntry[]> {
    const { data } = await this.client.get<{ history: StatusHistoryEntry[] }>(
      `/api/program-enrollments/${enrollmentId}/status-history`,
    );
    return data.history || [];
  }

  async remove(enrollmentId: number): Promise<void> {
    await this.client.delete(`/api/program-enrollments/${enrollmentId}`);
  }
}

export const programEnrollmentsAPI = new ProgramEnrollmentsAPI();
export default programEnrollmentsAPI;
